import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isAirportTransferReturnBeforePickup } from 'lib/airportTransfer/airportTransferUtils'
import moment from 'moment'
import { useMemo } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import { getTransferCoordinates } from 'lib/airportTransfer/coordinates'

export type TransferTripType = 'one-way' | 'return'

export interface TransferSearchParams {
  pickUpName?: string
  pickUpId?: string
  pickUpLat?: string
  pickUpLng?: string
  pickUpSearchType?: App.SearchType
  dropOffName?: string
  dropOffId?: string
  dropOffLat?: string
  dropOffLng?: string
  dropOffSearchType?: App.SearchType
  pickUpDate?: string
  pickUpTime?: string
  luggage?: string
  passengers?: string
  journeyType?: TransferTripType
  returnDate?: string
  returnTime?: string
  vehicleCategory?: string
  returnVehicleCategory?: string
  flightNumber?: string
  returnFlightNumber?: string
}

// Agent link params stay out, so a re-search from checkout does not preselect again.
export const TRANSFER_SEARCH_PARAM_KEYS = [
  'pickUpName', 'pickUpId', 'pickUpSearchType', 'pickUpLat', 'pickUpLng',
  'dropOffName', 'dropOffId', 'dropOffSearchType', 'dropOffLat', 'dropOffLng',
  'pickUpDate', 'pickUpTime', 'journeyType', 'passengers', 'luggage',
  'returnDate', 'returnTime',
] as const

export function getTransferSearchQueryString(searchParams: URLSearchParams): string {
  const transferParams = new URLSearchParams()

  for (const key of TRANSFER_SEARCH_PARAM_KEYS) {
    const value = searchParams.get(key)
    if (value !== null) transferParams.set(key, value)
  }

  return transferParams.toString()
}

export function serializeTransferSearchParams(params: TransferSearchParams): string {
  const searchParams = new URLSearchParams()

  for (const key of TRANSFER_SEARCH_PARAM_KEYS) {
    const value = params[key]
    if (value !== undefined) searchParams.set(key, value)
  }

  return searchParams.toString()
}

function getParamValue(searchParams: URLSearchParams, key: string): string | undefined {
  return searchParams.get(key) || undefined
}

function getJourneyType(searchParams: URLSearchParams): TransferTripType | undefined {
  const journeyType = getParamValue(searchParams, 'journeyType')

  return journeyType === 'one-way' || journeyType === 'return' ? journeyType : undefined
}

function getSearchType(searchParams: URLSearchParams, key: string): App.SearchType | undefined {
  const searchType = getParamValue(searchParams, key)

  return searchType === 'address' || searchType === 'airport' || searchType === 'property'
    ? searchType
    : undefined
}

function getDate(searchParams: URLSearchParams, key: string): string | undefined {
  const date = getParamValue(searchParams, key)

  if (!date || !moment(date, ISO_DATE_FORMAT, true).isValid()) return undefined

  return date
}

function getTime(searchParams: URLSearchParams, key: string): string | undefined {
  const time = getParamValue(searchParams, key)

  if (!time || !moment(time, 'HH:mm', true).isValid()) return undefined

  return time
}

function getCount(
  searchParams: URLSearchParams,
  key: string,
  minimum: number,
): string | undefined {
  const value = getParamValue(searchParams, key)

  if (!value || !/^\d+$/.test(value)) return undefined

  const count = Number(value)

  if (!Number.isSafeInteger(count) || count < minimum) return undefined

  return value
}

function getVehicleCategory(searchParams: URLSearchParams, key: string): string | undefined {
  const category = getParamValue(searchParams, key)?.trim().toUpperCase()

  return category && /^[A-Z_]+$/.test(category) ? category : undefined
}

function getFlightNumber(searchParams: URLSearchParams, key: string): string | undefined {
  const flightNumber = getParamValue(searchParams, key)?.replace(/\s+/g, '').toUpperCase()

  return flightNumber && /^[A-Z0-9]{2,8}$/.test(flightNumber) ? flightNumber : undefined
}

export function isAirportTransferAgentLinkSearch(search: string): boolean {
  return !!getVehicleCategory(new URLSearchParams(search), 'vehicleCategory')
}

export function parseTransferSearchParams(searchParams: URLSearchParams): TransferSearchParams {
  const pickupCoordinates = getTransferCoordinates(searchParams.get('pickUpLat'), searchParams.get('pickUpLng'))
  const dropoffCoordinates = getTransferCoordinates(searchParams.get('dropOffLat'), searchParams.get('dropOffLng'))
  const pickUpDate = getDate(searchParams, 'pickUpDate')
  const pickUpTime = getTime(searchParams, 'pickUpTime')
  let returnDate = getDate(searchParams, 'returnDate')
  let returnTime = getTime(searchParams, 'returnTime')
  let journeyType = getJourneyType(searchParams)
  const vehicleCategory = getVehicleCategory(searchParams, 'vehicleCategory')

  if (isAirportTransferReturnBeforePickup(
    pickUpDate,
    pickUpTime,
    returnDate,
    returnTime,
  )) {
    returnDate = undefined
    returnTime = undefined

    if (journeyType === 'return') {
      journeyType = 'one-way'
    }
  }

  return {
    pickUpName: getParamValue(searchParams, 'pickUpName'),
    pickUpId: getParamValue(searchParams, 'pickUpId'),
    pickUpLat: pickupCoordinates ? String(pickupCoordinates.lat) : undefined,
    pickUpLng: pickupCoordinates ? String(pickupCoordinates.lng) : undefined,
    pickUpSearchType: getSearchType(searchParams, 'pickUpSearchType'),
    dropOffName: getParamValue(searchParams, 'dropOffName'),
    dropOffId: getParamValue(searchParams, 'dropOffId'),
    dropOffLat: dropoffCoordinates ? String(dropoffCoordinates.lat) : undefined,
    dropOffLng: dropoffCoordinates ? String(dropoffCoordinates.lng) : undefined,
    dropOffSearchType: getSearchType(searchParams, 'dropOffSearchType'),
    pickUpDate,
    pickUpTime,
    luggage: getCount(searchParams, 'luggage', 0),
    passengers: getCount(searchParams, 'passengers', 1),
    journeyType,
    returnDate,
    returnTime,
    vehicleCategory,
    returnVehicleCategory: getVehicleCategory(searchParams, 'returnVehicleCategory') ?? vehicleCategory,
    flightNumber: getFlightNumber(searchParams, 'flightNumber'),
    returnFlightNumber: getFlightNumber(searchParams, 'returnFlightNumber'),
  }
}

export default function useTransferSearchParams(): TransferSearchParams {
  const searchParams = useQueryParams()

  return useMemo(() => parseTransferSearchParams(searchParams), [searchParams])
}
